import { useState } from "react";
import toast from "react-hot-toast";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import { Button } from "@shared/components/Button";
import { ConfirmDialog } from "@shared/components/ConfirmDialog";
import { useAsync } from "@shared/hooks/useAsync";
import { useUnsavedGuard } from "@shared/hooks/useUnsavedGuard";
import { getIntro, saveIntro } from "./intros.api";
import { IntroForm } from "./IntroForm";
import { fieldErrorsFrom, isDirty, toForm, toInput, validateForm, type FormErrors, type IntroFormValues } from "./introModel";
import type { IntroDetail } from "./types";

function EditorBody({ initial }: { initial: IntroDetail }) {
  const [saved, setSaved] = useState(initial);
  const [baseline, setBaseline] = useState<IntroFormValues>(() => toForm(initial));
  const [form, setForm] = useState<IntroFormValues>(baseline);
  const [errors, setErrors] = useState<FormErrors>({});
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const dirty = isDirty(form, baseline);
  const { blocker } = useUnsavedGuard(dirty);
  // An untouched placeholder can still be saved: saving without the box ticked approves it as real text.
  const canSave = !busy && (dirty || saved.isPlaceholder);

  async function save() {
    if (busy) return;
    const found = validateForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setNotice("Periksa kembali isian yang bertanda.");
      return;
    }
    setBusy(true);
    setNotice("");
    try {
      const stored = await saveIntro(saved.code, toInput(form));
      // Show what the server kept (HTML stripped, text trimmed), not what was typed.
      const next = toForm(stored);
      setSaved(stored);
      setBaseline(next);
      setForm(next);
      toast.success("Pengantar disimpan");
    } catch (err) {
      const fields = fieldErrorsFrom(err);
      if (Object.keys(fields).length > 0) {
        setErrors(fields);
        setNotice("Periksa kembali isian yang bertanda.");
      } else {
        setNotice(err instanceof Error ? err.message : "Terjadi kesalahan");
      }
      toast.error("Gagal menyimpan pengantar");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="editor">
      <Link to="/intros">← Kembali ke daftar</Link>
      <header className="page-head">
        <h2>Pengantar: {saved.name}</h2>
      </header>

      {!saved.hasIntro && <p className="muted">Belum ada pengantar untuk tes ini. Isi tagline dan ikhtisar lalu simpan.</p>}
      {saved.hasIntro && (
        <p className="muted">
          Terakhir diperbarui{" "}
          {saved.updatedAt ? format(new Date(saved.updatedAt), "d MMM yyyy HH:mm", { locale: idLocale }) : "-"} oleh{" "}
          {saved.updatedByUsername ?? "-"}.
        </p>
      )}
      {saved.isPlaceholder && (
        <p className="muted">Konten placeholder. Menyimpan tanpa mencentang kotak placeholder menandainya sebagai konten asli.</p>
      )}
      {notice && (
        <p role="alert" className="form-error">
          {notice}
        </p>
      )}

      <IntroForm values={form} errors={errors} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} />
      <div className="row actions">
        <Button disabled={!canSave} onClick={() => void save()}>
          {busy ? "Menyimpan…" : "Simpan"}
        </Button>
      </div>

      {blocker.state === "blocked" && (
        <ConfirmDialog
          title="Tinggalkan halaman?"
          confirmLabel="Tinggalkan"
          cancelLabel="Tetap di sini"
          destructive
          onConfirm={() => blocker.proceed()}
          onCancel={() => blocker.reset()}
        >
          Perubahan belum disimpan dan akan hilang.
        </ConfirmDialog>
      )}
    </section>
  );
}

function ExistingIntro({ code }: { code: string }) {
  const { data, error, errorStatus, loading, reload } = useAsync(() => getIntro(code), [code]);

  if (errorStatus === 404) {
    return (
      <section>
        <p>Pengantar tidak ditemukan.</p>
        <Link to="/intros">← Kembali ke daftar</Link>
      </section>
    );
  }
  if (error) {
    return (
      <section role="alert" className="form-error">
        <p>{error}</p>
        <Button variant="secondary" onClick={reload}>
          Coba lagi
        </Button>
      </section>
    );
  }
  if (loading || !data) return <p role="status">Memuat…</p>;
  return <EditorBody initial={data} />;
}

export function IntroEditorPage() {
  const { code = "" } = useParams();
  return <ExistingIntro key={code} code={code} />;
}
